<template>
  <div
    v-if="showUpsell && !upsellClicked"
    :class="bem({ upsellClicked, [upsellMode]: !parentItemHasLargerDiscount })"
    @click="addToCart"
  >
    <div :class="bem('content')">
      <div :class="bem('content-inner')">
        <DynamicImg
          :sku="upsell.product.sku"
          :width="200"
          :height="200"
          bg="ffffff"
          :class="bem('image')"
        />

        <div :class="bem('title-and-description')">
          <div :class="bem('title-and-price')">
            <VisionHeading
              size="h5"
              tag="h3"
              colour-style="gradient"
              :class="bem('title')"
            >
              {{ title }}
            </VisionHeading>
            <span
              v-if="!parentItemHasLargerDiscount && linkedDiscountedProductPrice !== 0"
              :class="bem('old-price')"
            >
              <s>{{ $tca(upsell.product.price) }}</s>
            </span>
            <span :class="bem('new-price')">
              <template v-if="discountedPrice === 0"> Free </template>
              <template v-else-if="discountedPrice && linkedDiscountedProductPrice !== 0">
                {{ $tca(discountedPrice) }}
              </template>
              <template v-else>
                {{ $tca(upsell.product.price) }}
              </template>
            </span>
          </div>
          <p :class="bem('description')">
            Add a Lens Cleaning Kit to care for your SunGods
          </p>
        </div>
      </div>
      <VisionLink
        :line-link="true"
        theme="gradient"
        :is-button="true"
        :class="bem('cta')"
      >
        Add
      </VisionLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import CustomisedProduct from '~/models/CustomisedProduct'
import { buildCartItems } from '~/assets/js/helpers/shopify'
const { $track, $sentry, $upsell } = useNuxtApp()

const bem = useBEM('CartItemCleaningKitUpsell')
const shopifyStore = useShopifyStore()

const props = defineProps({
  displayOrder: {
    type: Number,
    required: false,
    default: undefined,
  },
  customisedProduct: {
    type: CustomisedProduct,
    default: () => ({}),
  },
  cartItem: {
    type: Object,
    default: () => ({})
  }
})
const upsells = ref<any>(null)
const upsellClicked = ref<boolean>(false)

const upsell = computed(() => {
  return upsells.value?.find((upsell: any) => upsell.mode === 'simple' && upsell.product?.sku === 'lenscleankit')
})

const currency = computed(() => {
  return upsell.value.product.product.currency
})

const alreadyInCart = computed(() => {
  return !!shopifyStore.upsoldItems.find(item => item.summary.bundleSku === upsell.value.sku)
})

/**
 *
 * This checks if the discounted amount allocated to the parent cart item
 * is higher than the automatic discount for the lens cleanng kit for
 * limited edition products. If the limited edition product it is based off
 * is already discounted through an automatic discount you cannot have
 * another discount applied to a separate cart item that applies when the
 * other product (already discounted) is added to cart. Only the larger of
 * two will apply.
 *
 */
const parentItemHasLargerDiscount = computed(() => {
  return !!props.cartItem.bundleItems.find(bundleItem => {
    return bundleItem.node.discountAllocations?.find(discountAllocation => {
      return Number(discountAllocation.discountedAmount.amount) > discountedPrice.value
    })
  })
})

const showUpsell = computed(() => {
  if (!upsell.value) return false
  if (upsell.value.isInCart) return false

  // Only show in GBP, EUR and USD for McLaren products
  if (!['GBP', 'EUR', 'USD'].includes(currency.value)) return false

  if (!upsellMode.value) return false

  if (alreadyInCart.value) {
    return false
  }

  return true
})

const availableDiscount = computed(() => {
  return shopifyStore.getDiscountsForProductFromCartItems(
    upsell.value.product
  )?.[0]
})

const discountedPrice = computed(() => {
  if (availableDiscount.value) {
    if (availableDiscount.value.discountSummary.discountType === 'DiscountPercentage') {
      return upsell.value.product.price * (1 - availableDiscount.value.discountSummary.discountPercentageOff)
    }

    if (availableDiscount.value.discountSummary.discountType === 'DiscountAmount') {
      return upsell.value.product.price - availableDiscount.value.discountSummary.discountAmountOff
    }
  }

  return null
})

const linkedDiscountedProductPrice = computed(() => {
  return Object.values(shopifyStore.bundledCartItems).find(bundleItem => {
    return bundleItem.summary.bundleParentSku === props.customisedProduct.sku && bundleItem.summary.bundleCostDifference < 0
  })?.summary?.bundleCost
})

const isMcLarenProduct = computed(() => {
  const pattern = /mclaren|dr3|ln4/i
  return pattern.test(props.customisedProduct.sku)
})

const isWilliamsProduct = computed(() => {
  const pattern = /williams/i
  return pattern.test(props.customisedProduct.sku)
})

const isLimitedEdition = computed(() => {
  if (isMcLarenProduct.value) return false
  return props.customisedProduct.isLimitedEdition
})

const upsellMode: any = computed(() => {
  if (isWilliamsProduct.value) return 'williams'
  if (isLimitedEdition.value) return 'limited-edition'
  return null
})

const title = computed(() => {
  if (upsellMode.value === 'williams' && !parentItemHasLargerDiscount.value) {
    return 'Williams Exclusive'
  }
  return 'Care for your SunGods'
})

onBeforeMount(async () => {
  upsells.value = await $upsell.getUpsellsForProduct(props.customisedProduct)
})

async function addToCart() {
  upsellClicked.value = true

  const attributes = [
    {
      key: '_isUpsellItem',
      value: 'true',
    },
  ]

  if (props.displayOrder)
    attributes.push({
      key: '_bundleDisplayOrder',
      value: String(props.displayOrder + 0.5),
    })

  try {
    const newCartItems: any = buildCartItems(upsell.value.product, attributes)

    await shopifyStore.addCartItems(newCartItems)

    $track.product.add(upsell.value.product, {
      location: 'inline-cleaning-kit-upsell',
      quantity: 1,
    })
  } catch (error) {
    $sentry.captureException(error)
    console.log(error)
  }

  upsellClicked.value = false
}
</script>

<style lang="postcss">
.CartItemCleaningKitUpsell {
  @apply mt-2 relative p-px overflow-hidden
    transition-colors duration-200 ease-in-out cursor-pointer;

  &::after {
    @apply content-[''] absolute top-0 left-0
      w-full h-full bg-gradient
      -z-[1] rounded-[9px];
  }

  &--cart-updating {
    @apply opacity-50 cursor-not-allowed;
  }

  &--mclaren {
    --gradient: var(--gradient-orange);
  }
}

.CartItemCleaningKitUpsell__content {
  @apply p-2 pr-5  bg-white rounded-lg
  flex flex-row items-center justify-between gap-x-8;
}

.CartItemCleaningKitUpsell__content-inner {
  @apply flex gap-x-2 items-center;
}

.CartItemCleaningKitUpsell__image {
  @apply mr-2 max-w-[80px] rounded-md;
}

.CartItemCleaningKitUpsell__title {
  .CartItemCleaningKitUpsell--mclaren & {
    @apply t-gradient-orange !important;
  }
}

.CartItemCleaningKitUpsell__title-and-price {
  @apply flex gap-x-2 items-center;
}

.CartItemCleaningKitUpsell__title-and-description {
  @apply flex flex-col;
}

.CartItemCleaningKitUpsell__description {
  @apply text-xxs font-light;
}

.CartItemCleaningKitUpsell__old-price {
  @apply line-through text-xs text-grey-500;
}

.CartItemCleaningKitUpsell__new-price {
  @apply font-bold text-xs;
}
</style>
